import { useEffect, useMemo, useState } from "react";
import { isDemoMode } from "../firebase";
import { DEMO_ROLODEX as DEMO_ROLODEX_DATA } from "../demoData";
import {
  subscribeRolodex,
  subscribeRolodexEntry,
} from "../lib/rolodexStore";
import { usePublishedProfiles } from "./usePublishedProfiles";
import { samePublishedProfile } from "../lib/profilePublication";

// Re-exporting keeps demo consumers from needing to know which module owns the
// fixture while preserving the same hook/store contract as live mode.
export const DEMO_ROLODEX = DEMO_ROLODEX_DATA;

/** Live owner-only rolodex list, already enriched with registered profiles. */
export function useRolodex(ownerUid) {
  const [entries, setEntries] = useState(isDemoMode ? DEMO_ROLODEX : []);
  const [loading, setLoading] = useState(!isDemoMode && Boolean(ownerUid));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isDemoMode) {
      setEntries(DEMO_ROLODEX);
      setLoading(false);
      return undefined;
    }
    if (!ownerUid) {
      setEntries([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    return subscribeRolodex(
      ownerUid,
      (nextEntries) => {
        setEntries(nextEntries);
        setLoading(false);
      },
      (nextError) => {
        setError(nextError);
        setLoading(false);
      }
    );
  }, [ownerUid]);

  // Entries arrive enriched with a one-shot profile read. Overlay live
  // published profiles on top so that when someone edits their own profile —
  // a new industry, say — it appears here without a reload.
  const recipientUids = useMemo(
    () => entries.map((entry) => entry.recipient_uid).filter(Boolean),
    [entries]
  );
  const livePublishedProfiles = usePublishedProfiles(recipientUids);
  const liveEntries = useMemo(() => {
    if (!livePublishedProfiles.size) return entries;
    let changed = false;
    const next = entries.map((entry) => {
      const live = entry.recipient_uid
        ? livePublishedProfiles.get(entry.recipient_uid)
        : null;
      if (!live || samePublishedProfile(live, entry.otherProfile)) return entry;
      changed = true;
      return { ...entry, otherProfile: live };
    });
    return changed ? next : entries;
  }, [entries, livePublishedProfiles]);

  return { entries: liveEntries, loading, error };
}

/** Live owner-only subscription for a detail route. */
export function useRolodexEntry(ownerUid, entryId) {
  const demoEntry = isDemoMode
    ? DEMO_ROLODEX.find((entry) => entry.id === entryId) || null
    : null;
  const [entry, setEntry] = useState(demoEntry);
  const [loading, setLoading] = useState(
    !isDemoMode && Boolean(ownerUid && entryId)
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isDemoMode) {
      setEntry(DEMO_ROLODEX.find((item) => item.id === entryId) || null);
      setLoading(false);
      return undefined;
    }
    if (!ownerUid || !entryId) {
      setEntry(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    return subscribeRolodexEntry(
      ownerUid,
      entryId,
      (nextEntry) => {
        setEntry(nextEntry);
        setLoading(false);
      },
      (nextError) => {
        setError(nextError);
        setLoading(false);
      }
    );
  }, [ownerUid, entryId]);

  const liveProfiles = usePublishedProfiles(
    entry?.recipient_uid ? [entry.recipient_uid] : []
  );
  const liveEntry = useMemo(() => {
    if (!entry?.recipient_uid) return entry;
    const live = liveProfiles.get(entry.recipient_uid);
    if (!live || samePublishedProfile(live, entry.otherProfile)) return entry;
    return { ...entry, otherProfile: live };
  }, [entry, liveProfiles]);

  return { entry: liveEntry, loading, error };
}
